import { MamaApprovedBusiness } from '../../../mocks/mamaApproved';

interface Props {
  business: MamaApprovedBusiness;
  onClick: (business: MamaApprovedBusiness) => void;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <div key={star} className="w-3.5 h-3.5 flex items-center justify-center">
          <i
            className={`text-xs ${
              star <= Math.floor(rating)
                ? 'ri-star-fill text-amber-400'
                : star - 0.5 <= rating
                ? 'ri-star-half-fill text-amber-400'
                : 'ri-star-line text-gray-300'
            }`}
          />
        </div>
      ))}
    </div>
  );
}

export default function BusinessCard({ business, onClick }: Props) {
  return (
    <div
      onClick={() => onClick(business)}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 cursor-pointer group overflow-hidden flex flex-col"
    >
      {/* Logo area */}
      <div className="relative bg-gradient-to-br from-rose-50 to-amber-50 p-6 flex items-center justify-center h-36">
        <img
          src={business.logo}
          alt={business.name}
          className="w-20 h-20 object-cover rounded-xl shadow-md group-hover:scale-105 transition-transform duration-300"
        />
        {/* Badge - Featured Partner or Mama Approved */}
        {business.isFeaturedPartner ? (
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-rose-500 text-white px-2.5 py-1 rounded-full shadow-sm">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-star-fill text-xs"></i>
            </div>
            <span className="text-xs font-extrabold whitespace-nowrap">Featured Partner</span>
          </div>
        ) : (
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-amber-400 text-gray-900 px-2.5 py-1 rounded-full shadow-sm">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-medal-fill text-xs"></i>
            </div>
            <span className="text-xs font-extrabold whitespace-nowrap">Mama Approved</span>
          </div>
        )}
        {/* Category pill */}
        <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-white/90 backdrop-blur-sm text-pink-600 text-xs font-semibold rounded-full border border-pink-100">
          {business.category}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-base font-extrabold text-gray-900 mb-1 leading-snug group-hover:text-pink-600 transition-colors">
          {business.name}
        </h3>

        {/* Location */}
        <div className="flex items-center gap-1.5 mb-2">
          <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
            <i className="ri-map-pin-line text-gray-400 text-xs"></i>
          </div>
          <span className="text-xs text-gray-400">{business.location}</span>
        </div>

        <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2 flex-1">
          {business.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {business.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
              {tag}
            </span>
          ))}
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <StarRating rating={business.averageRating} />
          <span className="text-xs font-bold text-gray-800">{business.averageRating.toFixed(1)}</span>
          <span className="text-xs text-gray-400">({business.reviewCount} reviews)</span>
        </div>

        {/* Discount */}
        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 px-3 py-2.5 rounded-xl mb-4">
          <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
            <i className="ri-gift-2-line text-emerald-600 text-sm"></i>
          </div>
          <span className="text-xs font-semibold text-emerald-700 leading-snug">{business.memberDiscount}</span>
        </div>

        <button className="w-full py-2.5 bg-pink-600 text-white text-xs font-bold rounded-xl hover:bg-pink-700 transition-colors whitespace-nowrap cursor-pointer">
          View Details &amp; Reviews
        </button>
      </div>
    </div>
  );
}
